/**
 * `editing.ts` — whether the reader is in the middle of something a refresh would
 * take away.
 *
 * A `live` read (`api/hooks.ts`) asks again by itself, and what it brings back
 * redraws the page: a row that has moved off the loaded page is unmounted, and so is
 * a control the task's new status no longer offers. Whatever was open inside one — a
 * window with a half-written note in it, a date being typed into a cell — went with
 * it, without a word, a minute or less after it was opened and for a reason the
 * reader could not see. A refresh after the reader's own write never met this,
 * because a window is closed by the write that causes it.
 *
 * So the two things a refresh would interrupt say so while they are open
 * (`useEditing`), and every live read waits for them (`useAnyEditing`). It is a
 * **deferral** and nothing more: the ask that was skipped is made the moment the last
 * one closes (`api/hooks.ts` `useCatchUp`). A write made from inside a window that
 * **went through** still re-reads its module at once: the page reads what it just
 * wrote off that re-read. One that was **refused** leaves the live reads for the
 * same moment (`useApiMutation`), and every read that is not live is asked at once
 * either way — nothing here holds those.
 *
 * A module-level count rather than a context: the two sides are the design system's
 * `Dialog` and the API hooks, which share no provider, and a provider would be one
 * more thing every test harness has to remember to mount. A count rather than a
 * flag, because a confirmation opens over a window and closing it must not release
 * the window's own hold.
 *
 * **And the page behind a window is still** (`useStillness`). The hold stops the reads
 * that happen by themselves; it does not stop a read that one of the reader's own
 * writes asks for, and must not — that write has not landed until it has been read
 * again. But a window is not opened into an empty room: a batch is still going down
 * its rows, a date typed a moment ago is still on its way. Each is accepted, each
 * reads the list again, and the row the window belongs to — given to somebody, moved
 * out of the queue the list is filtered to — was unmounted by a write that was not
 * the window's, with the window and the paragraph in it. So what a `live` read *hands
 * the page* is held as it was when the window opened, whoever asks the cache to read
 * again and whatever comes back: the cache is current, every write lands when it
 * always did, and the page is drawn from it the moment the window shuts. A cell keeps
 * the value it has just saved for the same length of time (`afterWindows`) — the row
 * under it is the row from before.
 */

import { useEffect, useLayoutEffect, useSyncExternalStore } from 'react'

let open = 0
const listeners = new Set<() => void>()

/** How many windows are open, as distinct from a cell with the cursor in it. */
let windows = 0
/** Which stillness this is — one more for each time a first window opens — for `useStillness`. */
let stillness = 0
const windowListeners = new Set<() => void>()
/** What was asked to wait until no window is open (`afterWindows`). */
let waiting: (() => void)[] = []

function changed(): void {
  for (const listener of listeners) listener()
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

/** True while anything on the page is holding live reads still. Not a hook: read at the moment it is asked. */
export function anyEditing(): boolean {
  return open > 0
}

/**
 * Holds every live read still while `active` is true, and lets go when it is not —
 * or when the component that asked goes away, which is how a window closed by its
 * own row being removed still releases.
 *
 * `as: 'window'` is the design system's `Dialog`, and holds the page behind it still
 * as well (`useStillness`); a cell with the cursor in it is not one. The page is
 * modal under a window and is not under a cell, and a page that stood still while
 * the reader went on working in it would be showing them a move they had just made
 * as not made.
 */
export function useEditing(active: boolean, as: 'window' | 'cell' = 'cell'): void {
  useEffect(() => {
    if (!active) return
    open += 1
    changed()
    return () => {
      open -= 1
      changed()
    }
  }, [active])

  const windowed = active && as === 'window'
  /*
   * A layout effect, both ways. In: the page is held as it was drawn in the commit
   * the window opened in, before anything can be read under it. Out: what was read
   * under the window is drawn in the commit it shuts in — a frame later, and the row
   * a window had just changed was painted once more as it had been.
   */
  useLayoutEffect(() => {
    if (!windowed) return
    windows += 1
    if (windows === 1) stillness += 1
    for (const listener of windowListeners) listener()
    return () => {
      windows -= 1
      if (windows === 0) {
        const due = waiting
        waiting = []
        for (const one of due) one()
      }
      for (const listener of windowListeners) listener()
    }
  }, [windowed])
}

function subscribeToWindows(listener: () => void): () => void {
  windowListeners.add(listener)
  return () => {
    windowListeners.delete(listener)
  }
}

function whichStillness(): number {
  return windows === 0 ? 0 : stillness
}

function noStillness(): number {
  return 0
}

/**
 * Which stillness the page is being held in, or 0 while no window is open — as state.
 *
 * A number and not a yes: one window replaced by another in a single commit (Quick
 * create's "More details") is a page let go and held again with nothing drawn in
 * between, and a reader of this has to be able to tell the second from the first —
 * what it kept for the first is from before whatever the first window did.
 *
 * `watching: false` answers 0 and never re-renders, as `useAnyEditing` does and for
 * its reason.
 */
export function useStillness(watching: boolean): number {
  const answer = watching ? whichStillness : noStillness
  return useSyncExternalStore(subscribeToWindows, answer, answer)
}

/**
 * `then`, once no window is open: now, or as the last one shuts — in the same turn as
 * the page behind it is let go, so that the two are drawn together.
 *
 * For what a control shows *instead of* its row: the value a cell has just saved. The
 * row under an open window is the row from before the save, and a cell that let go
 * of its value as the save landed showed the old one until the window shut — a saved
 * date reading as not saved.
 */
export function afterWindows(then: () => void): void {
  if (windows === 0) {
    then()
    return
  }
  waiting.push(then)
}

/**
 * `anyEditing`, as state: the component re-renders when the answer changes.
 *
 * `watching: false` answers false and never re-renders. Every read in the app goes
 * through the two hooks that ask this and only the live ones act on the answer, so
 * without it opening any window anywhere re-rendered every component holding a read.
 */
export function useAnyEditing(watching: boolean): boolean {
  const answer = watching ? anyEditing : nobodyEditing
  return useSyncExternalStore(subscribe, answer, answer)
}

function nobodyEditing(): boolean {
  return false
}
